/** Parsed PGN tag headers used for chess game journal entries. */

export type ChessPgnMeta = {
  dateIso: string | null;
  white: string | null;
  black: string | null;
};

export type ChessPlayerColor = 'white' | 'black';

const HEADER_RE = /^\s*\[(\w+)\s+"([^"]*)"\]\s*$/;

/** Read standard PGN tags from the header block (before the first move line). */
export function parseChessPgnHeaders(pgn: string): ChessPgnMeta {
  let white: string | null = null;
  let black: string | null = null;
  let dateRaw: string | null = null;

  for (const line of pgn.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (/^\d+\./.test(trimmed) || trimmed.startsWith('{')) break;
    const m = HEADER_RE.exec(trimmed);
    if (!m) continue;
    const key = m[1]!.toLowerCase();
    const value = m[2]!.trim();
    if (key === 'white') white = value || null;
    else if (key === 'black') black = value || null;
    else if (key === 'date' || key === 'utcdate') dateRaw = value || null;
  }

  return {
    dateIso: normalizePgnDate(dateRaw),
    white,
    black,
  };
}

/** Convert PGN date tags (YYYY.MM.DD, YYYY-MM-DD, etc.) to ISO date. */
export function normalizePgnDate(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const s = raw.trim().replace(/\./g, '-').replace(/\//g, '-');
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (!m) return null;
  const y = m[1]!;
  const mo = m[2]!.padStart(2, '0');
  const d = m[3]!.padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

function opponentName(meta: ChessPgnMeta, playerColor: ChessPlayerColor): string | null {
  if (playerColor === 'white') return meta.black;
  return meta.white;
}

/**
 * First line of a journal item: date and opponent (or matchup), then a period.
 * Example: "2024-06-03 Magnus Carlsen." or "2024-06-03. Alice vs Bob."
 */
export function formatChessGameItemHeadline(
  meta: ChessPgnMeta,
  playerColor: ChessPlayerColor | null,
  fallbackDateIso?: string
): string {
  const date = meta.dateIso ?? fallbackDateIso ?? new Date().toISOString().slice(0, 10);

  if (playerColor) {
    const opp = opponentName(meta, playerColor);
    if (opp) return `${date} ${opp}.`;
    const self = playerColor === 'white' ? meta.white : meta.black;
    if (self) return `${date} (${self}).`;
    return `${date}.`;
  }

  if (meta.white && meta.black) return `${date}. ${meta.white} vs ${meta.black}.`;
  if (meta.white) return `${date}. ${meta.white}.`;
  if (meta.black) return `${date}. ${meta.black}.`;
  return `${date}.`;
}
